import { GPXData, AidStation, RouteSegment } from '../types';

export const calculateSegments = (
  gpxData: GPXData,
  aidStations: AidStation[]
): RouteSegment[] => {
  if (!gpxData || !gpxData.coordinates || gpxData.coordinates.length < 2 || aidStations.length === 0) {
    return [];
  }

  const sortedStations = [...aidStations].sort((a, b) => a.distance - b.distance);
  const segments: RouteSegment[] = [];
  let currentDistance = 0;

  // Calculate cumulative distances for each point
  const distances: number[] = [0]; // Start with 0 distance for the first point
  for (let i = 1; i < gpxData.coordinates.length; i++) {
    const prev = gpxData.coordinates[i - 1];
    const curr = gpxData.coordinates[i];
    // Pass lat (index 1) and lon (index 0) to calculateDistance
    const distance = calculateDistance([prev[1], prev[0]], [curr[1], curr[0]]);
    currentDistance += distance;
    distances.push(currentDistance);
  }

  // Add a small check for the case where distances might not be calculated
  if (distances.length !== gpxData.coordinates.length) {
      console.error("Distance calculation mismatch with coordinate points.");
      return []; // Or handle error appropriately
  }

  // Create segments between aid stations
  for (let i = 0; i < sortedStations.length; i++) {
    const startStationDistance = i === 0 ? 0 : sortedStations[i - 1].distance;
    const endStationDistance = sortedStations[i].distance;

    // Find the indices in the coordinates array that correspond to these distances
    // Ensure indices cover the range correctly
    let startIndex = distances.findIndex(d => d >= startStationDistance);
    let endIndex = distances.findIndex(d => d >= endStationDistance);

    // Handle edge cases where distances might not exactly match
    if (startIndex === -1) startIndex = 0;
    if (endIndex === -1) endIndex = gpxData.coordinates.length - 1;
    // Ensure endIndex is at least startIndex
    if (endIndex < startIndex) endIndex = startIndex;
    // Ensure the slice includes the point at endIndex
    const segmentCoordinates = gpxData.coordinates.slice(startIndex, endIndex + 1);

    // Recalculate actual segment distance from its coordinates if needed, or use station distances
    const segmentDistance = endStationDistance - startStationDistance;

    segments.push({
      id: `segment-${i}`,
      startDistance: startStationDistance,
      endDistance: endStationDistance,
      coordinates: segmentCoordinates,
      color: getSegmentColor(i),
      name: `${sortedStations[i].name} Segment`, // Use station name for segment name
      distance: segmentDistance,
      description: `From ${i === 0 ? 'Start' : sortedStations[i - 1].name} to ${sortedStations[i].name}` // More descriptive
    });
  }

  return segments;
};

const calculateDistance = (point1: [number, number], point2: [number, number]): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(point2[0] - point1[0]); // point[0] is Latitude
  const dLon = toRad(point2[1] - point1[1]); // point[1] is Longitude
  const lat1 = toRad(point1[0]);
  const lat2 = toRad(point2[0]);

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c * 1000; // Return distance in meters to be consistent with simplifyGPXData
};

const toRad = (value: number): number => {
  return value * Math.PI / 180;
};

const getSegmentColor = (index: number): string => {
  const colors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FFA500', // Orange
    '#800080', // Purple
    '#008000', // Dark Green
    '#000080', // Navy
  ];
  return colors[index % colors.length];
}; 